import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Table, Badge } from 'react-bootstrap';
import { Booking } from '../booking';
import { GoogleMap, useJsApiLoader, DirectionsRenderer, Marker } from '@react-google-maps/api';
import type { Library } from '@googlemaps/js-api-loader';

interface ViewFinalBookingModalProps {
  show: boolean;
  onHide: () => void;
  booking: Booking | null;
  statusColors: { [key: string]: string };
}

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  'AIzaSyC7S0DKmKpZmtidyMto2PiIXhR5miu9Pi0';
const GOOGLE_MAPS_LIBRARIES: Library[] = ['places'];

const center = { lat: 6.9271, lng: 79.8612 }; // Default center (Colombo, Sri Lanka)

const ViewFinalBookingModal: React.FC<ViewFinalBookingModalProps> = ({
  show,
  onHide,
  booking,
  statusColors,
}) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const [directions, setDirections] = useState<google.maps.DirectionsResult | null>(null);

  // Load Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Helper function to format timestamp or date string to readable date
  const formatDate = (input: number | string | null | undefined): string => {
    if (!input) return 'N/A';

    let date: Date;
    if (typeof input === 'number') {
      date = new Date(input);
    } else {
      date = new Date(input); // Parse string input
    }

    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }

    return date.toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  // Helper function to format destinations (only addresses)
  const renderDestinations = (locations: Booking['locations']): JSX.Element => {
    if (!locations || locations.length === 0) {
      return <tr><td colSpan={2}>No destinations available</td></tr>;
    }
    return (
      <>
        {locations.map((location, index) => (
          <tr key={location.id}>
            <td>{index + 1}</td>
            <td>{location.address || 'N/A'}</td>
          </tr>
        ))}
      </>
    );
  };

  // Calculate route for Google Map
  const calculateRoute = useCallback(async () => {
    if (!isLoaded || !booking || !booking.locations || booking.locations.length < 2) {
      return;
    }

    const directionsService = new google.maps.DirectionsService();
    const waypoints = booking.locations
      .slice(1, -1) // Exclude start and return locations for waypoints
      .map((loc) => ({
        location: new google.maps.LatLng(parseFloat(loc.lat), parseFloat(loc.lng)),
        stopover: true,
      }));

    const origin = booking.locations[0]; // First location as origin
    const destination = booking.locations[booking.locations.length - 1]; // Last location as destination

    if (!origin || !destination || !origin.lat || !origin.lng || !destination.lat || !destination.lng) {
      return;
    }

    try {
      const result = await directionsService.route({
        origin: new google.maps.LatLng(parseFloat(origin.lat), parseFloat(origin.lng)),
        destination: new google.maps.LatLng(parseFloat(destination.lat), parseFloat(destination.lng)),
        waypoints,
        travelMode: google.maps.TravelMode.DRIVING,
        optimizeWaypoints: true,
      });

      setDirections(result);

      if (mapRef.current && result.routes[0]) {
        const bounds = new google.maps.LatLngBounds();
        result.routes[0].overview_path.forEach((point) => bounds.extend(point));
        mapRef.current.fitBounds(bounds);
      }
    } catch (error: any) {
    }
  }, [isLoaded, booking]);

  // Run calculateRoute when modal is shown and locations are available
  useEffect(() => {
    if (show && isLoaded && booking && booking.locations?.some((loc) => loc.lat && loc.lng)) {
      calculateRoute();
    }
  }, [show, isLoaded, booking, calculateRoute]);

  if (!booking) {
    return null; // Return null if no booking is provided
  }

  if (loadError) {
    return (
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        autoFocus={false}
        enforceFocus={false}
        aria-labelledby="view-final-booking"
      >
        <Modal.Header closeButton>
          <Modal.Title id="view-final-booking">Booking Details - ID: {booking.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-danger">Error loading Google Maps: {loadError.message}</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      autoFocus={false}
      enforceFocus={false}
      aria-labelledby="view-final-booking"
    >
      <Modal.Header closeButton>
        <Modal.Title id="view-final-booking">Booking Details - ID: {booking.id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="booking-details">
          <div className="row">
            <div className="col-md-6">
              <Table bordered hover>
                <tbody>
                  <tr>
                    <th>Booking ID</th>
                    <td>{booking.id || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>User</th>
                    <td>
                      {booking.user
                        ? `${booking.user.name || 'N/A'} (${booking.user.empNumber || 'N/A'})`
                        : 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <th>Email</th>
                    <td>{booking.user?.email || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Reason</th>
                    <td>{booking.reason || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Status</th>
                    <td>
                      <Badge bg={statusColors[booking.status] || 'secondary'}>
                        {booking.status || 'N/A'}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <th>Priority</th>
                    <td>{booking.priority || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Booking Type</th>
                    <td>{booking.bookingType || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>No. of Passengers</th>
                    <td>{booking.noOfPassengers || 'N/A'}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
            <div className="col-md-6">
              <Table bordered hover>
                <tbody>
                  <tr>
                    <th>Departure Time</th>
                    <td>{formatDate(booking.departureTime)}</td>
                  </tr>
                  <tr>
                    <th>Arrival Time</th>
                    <td>{formatDate(booking.arrivalTime)}</td>
                  </tr>
                  <tr>
                    <th>Estimated Distance (km)</th>
                    <td>{booking.estimatedKms ? booking.estimatedKms.toFixed(2) : 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Intermediate Approved By</th>
                    <td>
                      {booking.intermediateApprovedBy
                        ? `${booking.intermediateApprovedBy.name || 'N/A'} (${
                            booking.intermediateApprovedBy.empNumber || 'N/A'
                          })`
                        : 'N/A'}
                    </td>
                  </tr>
                  <tr>
                    <th>Intermediate Approval Date</th>
                    <td>{booking.intermediateApprovedDate || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Comment</th>
                    <td>{booking.comment || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Created By</th>
                    <td>{booking.createdBy || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Created At</th>
                    <td>{formatDate(booking.createdAt)}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>

          <h5>Destinations</h5>
          <div className="row">
            <div className="col-md-6">
              <Table bordered hover>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Address</th>
                  </tr>
                </thead>
                <tbody>{renderDestinations(booking.locations)}</tbody>
              </Table>
            </div>
            <div className="col-md-6">
              <h6>Route Map</h6>
              {isLoaded ? (
                <GoogleMap
                  mapContainerStyle={{ height: '300px', width: '100%' }}
                  center={center}
                  zoom={12}
                  onLoad={(map) => {
                    mapRef.current = map;
                  }}
                >
                  {booking.locations.map((location, index) => (
                    location.lat &&
                    location.lng && (
                      <Marker
                        key={index}
                        position={{ lat: parseFloat(location.lat), lng: parseFloat(location.lng) }}
                        title={`Location ${index + 1}`}
                      />
                    )
                  ))}
                  {directions && (
                    <DirectionsRenderer
                      directions={directions}
                      options={{
                        polylineOptions: {
                          strokeColor: '#1976D2',
                          strokeOpacity: 0.8,
                          strokeWeight: 5,
                        },
                      }}
                    />
                  )}
                </GoogleMap>
              ) : (
                <div>Loading Google Maps...</div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewFinalBookingModal;